import React, { useState } from "react";
import { useApi } from "../hooks/useApi";
import Button from "./Buttons";
import { X } from "lucide-react";

const formatImpact = (val) => {
    if (val == null) return "\u2014";
    const n = Number(val);
    if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
    return `$${Math.round(n).toLocaleString()}`;
};

const SCENARIO_TABS = [
    { id: "remediate", label: "Remediate Finding" },
    { id: "overrideRpi", label: "Override RPI" },
    { id: "changeImportance", label: "Change Importance" },
];

const VendorWhatIfExplorer = ({ vendorId, isOpen, onClose, risks }) => {
    const apiCall = useApi();
    const [scenarioType, setScenarioType] = useState("remediate");
    const [selectedRiskId, setSelectedRiskId] = useState("");
    const [rpiMultiplier, setRpiMultiplier] = useState("1.0");
    const [dataSensitivity, setDataSensitivity] = useState("");
    const [accessScope, setAccessScope] = useState("");
    const [revenueDependency, setRevenueDependency] = useState("");
    const [running, setRunning] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);

    if (!isOpen) return null;

    const handleRun = async () => {
        setRunning(true);
        setError(null);
        setResult(null);
        try {
            let payload = {};
            if (scenarioType === "remediate") {
                payload = { scenarioType: "RemediateFinding", findingId: selectedRiskId };
            } else if (scenarioType === "overrideRpi") {
                payload = { scenarioType: "OverrideRpi", rpiMultiplier: parseFloat(rpiMultiplier) };
            } else if (scenarioType === "changeImportance") {
                payload = {
                    scenarioType: "ChangeImportance",
                    newDataSensitivity: dataSensitivity || null,
                    newAccessScope: accessScope || null,
                    newRevenueDependency: revenueDependency || null,
                };
            }
            const res = await apiCall("POST", `/TPRMVendor/${vendorId}/what-if`, payload);
            setResult(res);
        } catch (err) {
            setError(err?.message || "Failed to run scenario");
        } finally {
            setRunning(false);
        }
    };

    const canRun = () => {
        if (scenarioType === "remediate") return !!selectedRiskId;
        if (scenarioType === "overrideRpi") {
            const val = parseFloat(rpiMultiplier);
            return !isNaN(val) && val >= 0.01 && val <= 100;
        }
        if (scenarioType === "changeImportance") return dataSensitivity || accessScope || revenueDependency;
        return false;
    };

    const severityColor = (sev) => {
        switch ((sev || "").toUpperCase()) {
            case "CRITICAL": return "text-red-700";
            case "HIGH": return "text-orange-600";
            case "MEDIUM": return "text-yellow-600";
            case "LOW": return "text-green-600";
            default: return "text-gray-600";
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-[600px] max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-200">
                    <h2 className="text-lg font-semibold">What-If Explorer</h2>
                    <button onClick={onClose} className="p-1 rounded hover:bg-gray-100">
                        <X className="h-5 w-5 text-gray-500" />
                    </button>
                </div>

                <div className="p-4">
                    <p className="text-sm text-gray-500 mb-4">
                        Explore counterfactual scenarios to understand how specific actions would change this vendor's risk profile before committing to a course of action. Remediate Finding re-runs the 5,000-iteration Monte Carlo simulation behind the Axaris Vendor Impact Index, with and without the finding; Override RPI and Change Importance rescale that stored simulation.
                    </p>

                    {/* Scenario type tabs */}
                    <div className="flex items-center gap-1 mb-4 bg-gray-100 rounded-lg p-1">
                        {SCENARIO_TABS.map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => { setScenarioType(tab.id); setResult(null); setError(null); }}
                                className={`flex-1 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                                    scenarioType === tab.id
                                        ? "bg-white text-gray-900 shadow-sm"
                                        : "text-gray-500 hover:text-gray-700"
                                }`}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>

                    {/* Scenario inputs */}
                    <div className="mb-4">
                        {scenarioType === "remediate" && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Select Risk to Remediate</label>
                                <select
                                    value={selectedRiskId}
                                    onChange={(e) => setSelectedRiskId(e.target.value)}
                                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                                >
                                    <option value="">-- Select a risk --</option>
                                    {(risks || []).map((risk) => (
                                        <option key={risk.id} value={risk.id}>
                                            {risk.riskTitle} ({risk.riskSeverity})
                                        </option>
                                    ))}
                                </select>
                                {selectedRiskId && (() => {
                                    const risk = (risks || []).find(r => r.id === selectedRiskId);
                                    if (!risk) return null;
                                    return (
                                        <div className="mt-2 p-2 bg-gray-50 rounded text-sm">
                                            <span className={`font-medium ${severityColor(risk.riskSeverity)}`}>{risk.riskSeverity}</span>
                                            <span className="text-gray-500 ml-2">{risk.riskTitle}</span>
                                        </div>
                                    );
                                })()}
                            </div>
                        )}

                        {scenarioType === "overrideRpi" && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">RPI Multiplier (0.01 – 100)</label>
                                <input
                                    type="number"
                                    min="0.01"
                                    max="100"
                                    step="0.01"
                                    value={rpiMultiplier}
                                    onChange={(e) => setRpiMultiplier(e.target.value)}
                                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                                />
                                <p className="text-xs text-gray-500 mt-1">Values below 1.0 reduce ransomware prediction; above 1.0 increase it.</p>
                            </div>
                        )}

                        {scenarioType === "changeImportance" && (
                            <div className="space-y-3">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Data Sensitivity</label>
                                    <select
                                        value={dataSensitivity}
                                        onChange={(e) => setDataSensitivity(e.target.value)}
                                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                                    >
                                        <option value="">-- No change --</option>
                                        <option value="PHI">PHI</option>
                                        <option value="PII">PII</option>
                                        <option value="Financial">Financial</option>
                                        <option value="Public">Public</option>
                                        <option value="None">None</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Access Scope</label>
                                    <select
                                        value={accessScope}
                                        onChange={(e) => setAccessScope(e.target.value)}
                                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                                    >
                                        <option value="">-- No change --</option>
                                        <option value="NetworkAccess">Network Access</option>
                                        <option value="DataProcessor">Data Processor</option>
                                        <option value="SaaSOnly">SaaS Only</option>
                                        <option value="Physical">Physical Access</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Revenue Dependency</label>
                                    <select
                                        value={revenueDependency}
                                        onChange={(e) => setRevenueDependency(e.target.value)}
                                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                                    >
                                        <option value="">-- No change --</option>
                                        <option value="Critical">Critical</option>
                                        <option value="Important">Important</option>
                                        <option value="Low">Low</option>
                                    </select>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Run button */}
                    <Button
                        onClick={handleRun}
                        disabled={running || !canRun()}
                        className="w-full"
                    >
                        {running ? "Running Scenario..." : "Run Scenario"}
                    </Button>

                    {/* Error */}
                    {error && (
                        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                            {error}
                        </div>
                    )}

                    {/* Results */}
                    {result && (
                        <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                            <h4 className="text-sm font-semibold text-blue-800 mb-3">Scenario Results</h4>

                            {/* FAIR EAL (primary) */}
                            {result.currentFairEAL != null && result.currentFairEAL > 0 && (
                                <div className="grid grid-cols-2 gap-4 mb-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                                    <div>
                                        <div className="text-xs text-emerald-600 font-semibold">Current EAL Estimate</div>
                                        <div className="text-lg font-bold text-emerald-800">{formatImpact(result.currentFairEAL)}</div>
                                    </div>
                                    <div>
                                        <div className="text-xs text-emerald-600 font-semibold">Projected EAL Estimate</div>
                                        <div className="text-lg font-bold text-emerald-700">{formatImpact(result.projectedFairEAL)}</div>
                                    </div>
                                    <div>
                                        <div className="text-xs text-emerald-600 font-semibold">EAL Reduction</div>
                                        <div className={`text-lg font-bold ${(result.fairEALDelta || 0) < 0 ? "text-emerald-700" : "text-red-700"}`}>
                                            {formatImpact(result.fairEALDelta)}
                                        </div>
                                    </div>
                                    <div>
                                        <div className="text-xs text-emerald-600 font-semibold">Reduction %</div>
                                        <div className={`text-lg font-bold ${(result.fairEALDeltaPercent || 0) < 0 ? "text-emerald-700" : "text-red-700"}`}>
                                            {result.fairEALDeltaPercent != null ? `${result.fairEALDeltaPercent.toFixed(1)}%` : "\u2014"}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* AVI (supporting detail) */}
                            <div className="grid grid-cols-2 gap-4 mb-3">
                                <div>
                                    <div className="text-xs text-gray-500">Current AVI</div>
                                    <div className="text-lg font-bold text-gray-800">{formatImpact(result.currentAvi)}{result.currentAvi != null ? '+' : ''}</div>
                                </div>
                                <div>
                                    <div className="text-xs text-gray-500">Projected AVI</div>
                                    <div className="text-lg font-bold text-blue-800">{formatImpact(result.projectedAvi)}{result.projectedAvi != null ? '+' : ''}</div>
                                </div>
                                <div>
                                    <div className="text-xs text-gray-500">AVI Delta</div>
                                    <div className={`text-lg font-bold ${(result.aviDelta || 0) < 0 ? "text-emerald-700" : "text-red-700"}`}>
                                        {formatImpact(result.aviDelta)}{result.aviDelta != null ? '+' : ''}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-xs text-gray-500">Delta %</div>
                                    <div className={`text-lg font-bold ${(result.aviDeltaPercent || 0) < 0 ? "text-emerald-700" : "text-red-700"}`}>
                                        {result.aviDeltaPercent != null ? `${result.aviDeltaPercent.toFixed(1)}%` : "\u2014"}
                                    </div>
                                </div>
                            </div>
                            {result.ealChange != null && (
                                <div className="mb-3">
                                    <div className="text-xs text-gray-500">MC EAL Change</div>
                                    <div className="text-sm font-medium">{formatImpact(result.ealChange)}</div>
                                </div>
                            )}
                            {result.explanation && (
                                <div className="p-3 bg-white rounded border border-blue-100">
                                    <div className="text-xs font-semibold text-gray-600 mb-1">AI Explanation</div>
                                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{result.explanation}</p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default VendorWhatIfExplorer;
